/**
 * Reading and patching a ComfyUI workflow.
 *
 * Workflows are exported in "API format": a flat map of node id -> node, where a
 * node's input is either a literal or a `[nodeId, slot]` link. There is no fixed
 * schema for *which* node holds the prompt or the input image - that depends on
 * how the graph was built - so the workbench needs a binding: which node and
 * which input to write each value into.
 *
 * `suggestBindings` guesses that from the graph structure, which gets a normal
 * text-to-image or img2img workflow working with no configuration. The bindings
 * stay editable because the guess cannot always be right.
 */

export interface ComfyNode {
  class_type: string;
  inputs: Record<string, unknown>;
  _meta?: { title?: string };
}

export type ComfyWorkflow = Record<string, ComfyNode>;

export interface WorkflowBindings {
  imageNode?: string;
  imageInput?: string;
  maskNode?: string;
  maskInput?: string;
  promptNode?: string;
  promptInput?: string;
  negativeNode?: string;
  negativeInput?: string;
  seedNode?: string;
  seedInput?: string;
}

export interface WorkflowValues {
  image?: string;
  mask?: string;
  prompt?: string;
  negativePrompt?: string;
  seed?: number;
}

/** Node classes that load an image from the input folder. */
const IMAGE_LOADERS = ['LoadImage', 'LoadImageOutput', 'ETN_LoadImageBase64'];
const MASK_LOADERS = ['LoadImageMask', 'ETN_LoadMaskBase64'];
const TEXT_ENCODERS = ['CLIPTextEncode', 'CLIPTextEncodeSDXL', 'T5TextEncode'];
const SAMPLERS = ['KSampler', 'KSamplerAdvanced', 'SamplerCustom', 'SamplerCustomAdvanced'];

function nodesOfClass(workflow: ComfyWorkflow, classes: readonly string[]): string[] {
  return Object.entries(workflow)
    .filter(([, node]) => classes.includes(node.class_type))
    .map(([id]) => id);
}

/** Follows a `[nodeId, slot]` link, if that input is one. */
function linkTarget(node: ComfyNode | undefined, input: string): string | undefined {
  const value = node?.inputs?.[input];
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return undefined;
}

export interface WorkflowInspection {
  imageLoaders: string[];
  maskLoaders: string[];
  textEncoders: string[];
  samplers: string[];
  /** Every node id with its class, for building a picker. */
  nodes: Array<{ id: string; classType: string; title?: string }>;
}

export function inspectWorkflow(workflow: ComfyWorkflow): WorkflowInspection {
  return {
    imageLoaders: nodesOfClass(workflow, IMAGE_LOADERS),
    maskLoaders: nodesOfClass(workflow, MASK_LOADERS),
    textEncoders: nodesOfClass(workflow, TEXT_ENCODERS),
    samplers: nodesOfClass(workflow, SAMPLERS),
    nodes: Object.entries(workflow).map(([id, node]) => ({
      id,
      classType: node.class_type,
      ...(node._meta?.title ? { title: node._meta.title } : {}),
    })),
  };
}

/**
 * Guesses bindings from the graph.
 *
 * Positive and negative prompts are told apart by walking the sampler's
 * `positive` / `negative` links rather than by node order or title, which is the
 * only way that survives a rearranged graph.
 */
export function suggestBindings(workflow: ComfyWorkflow): WorkflowBindings {
  const found = inspectWorkflow(workflow);
  const bindings: WorkflowBindings = {};

  const imageNode = found.imageLoaders[0];
  if (imageNode) {
    bindings.imageNode = imageNode;
    bindings.imageInput = 'image';
  }

  const maskNode = found.maskLoaders[0] ?? found.imageLoaders[1];
  if (maskNode) {
    bindings.maskNode = maskNode;
    bindings.maskInput = 'image';
  }

  const sampler = found.samplers[0];
  if (sampler) {
    const node = workflow[sampler];
    if (node && 'seed' in node.inputs) {
      bindings.seedNode = sampler;
      bindings.seedInput = 'seed';
    } else if (node && 'noise_seed' in node.inputs) {
      bindings.seedNode = sampler;
      bindings.seedInput = 'noise_seed';
    }

    const positive = linkTarget(node, 'positive');
    const negative = linkTarget(node, 'negative');
    if (positive && found.textEncoders.includes(positive)) {
      bindings.promptNode = positive;
      bindings.promptInput = 'text';
    }
    if (negative && found.textEncoders.includes(negative)) {
      bindings.negativeNode = negative;
      bindings.negativeInput = 'text';
    }
  }

  // No sampler to disambiguate: fall back to the first encoder as positive.
  if (!bindings.promptNode && found.textEncoders[0]) {
    bindings.promptNode = found.textEncoders[0];
    bindings.promptInput = 'text';
  }

  return bindings;
}

/**
 * Returns a copy of the workflow with the bound inputs replaced.
 *
 * Pure: the configured workflow is never mutated, so a failed run does not leave
 * the next one carrying a stale filename.
 */
export function applyBindings(
  workflow: ComfyWorkflow,
  bindings: WorkflowBindings,
  values: WorkflowValues,
): ComfyWorkflow {
  const patched: ComfyWorkflow = {};
  for (const [id, node] of Object.entries(workflow)) {
    patched[id] = { ...node, inputs: { ...node.inputs } };
  }

  const set = (nodeId: string | undefined, input: string | undefined, value: unknown): void => {
    if (!nodeId || value === undefined) return;
    const node = patched[nodeId];
    if (!node) return;
    node.inputs[input ?? 'value'] = value;
  };

  set(bindings.imageNode, bindings.imageInput ?? 'image', values.image);
  set(bindings.maskNode, bindings.maskInput ?? 'image', values.mask);
  set(bindings.promptNode, bindings.promptInput ?? 'text', values.prompt);
  set(bindings.negativeNode, bindings.negativeInput ?? 'text', values.negativePrompt);
  set(bindings.seedNode, bindings.seedInput ?? 'seed', values.seed);

  return patched;
}

/** Parses a workflow, rejecting the UI export format with a usable message. */
export function parseWorkflow(text: string): ComfyWorkflow {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (cause) {
    throw new Error(`Workflow is not valid JSON: ${cause instanceof Error ? cause.message : cause}`);
  }
  if (typeof value !== 'object' || value === null) {
    throw new Error('Workflow must be a JSON object.');
  }
  // The UI's "Save" format has `nodes` / `links` arrays; the API format is the
  // flat node map, and it is the only one /prompt accepts.
  if ('nodes' in value && Array.isArray((value as { nodes: unknown }).nodes)) {
    throw new Error(
      'This is the ComfyUI editor format. Export with "Save (API format)" - enable it under Settings > Enable Dev mode options.',
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) throw new Error('Workflow is empty.');
  for (const [id, node] of entries) {
    if (typeof node !== 'object' || node === null || !('class_type' in node)) {
      throw new Error(`Node "${id}" has no class_type; this does not look like an API-format workflow.`);
    }
  }
  return value as ComfyWorkflow;
}
